import type { PaymentResult, Platform, PlatformViewport, SharePayload } from './types';
import { subscribeDocumentVisibility } from './visibility';

/**
 * Обёртка над `window.Telegram.WebApp` — глобалом, который добавляет
 * официальный скрипт `https://telegram.org/js/telegram-web-app.js`
 * (подключён в index.html, см. Bot API docs: core.telegram.org/bots/webapps).
 *
 * Типы объявлены здесь вручную и намеренно узкие: полного SDK не
 * устанавливаем, чтобы не тащить в клиент поверхность API, которой
 * продукт не пользуется.
 */

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { start_param?: string; user?: { allows_write_to_pm?: boolean } };
  colorScheme: 'light' | 'dark';
  viewportHeight: number;
  viewportStableHeight: number;
  isExpanded: boolean;
  ready(): void;
  expand(): void;
  close(): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  openTelegramLink(url: string): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  /** Bot API 6.9+; на более старых клиентах метода нет. */
  requestWriteAccess?(callback?: (allowed: boolean) => void): void;
  /** Bot API 7.4+; на более старых клиентах метода нет. */
  openInvoice?(url: string, callback?: (status: string) => void): void;
  onEvent(event: string, handler: () => void): void;
  offEvent(event: string, handler: () => void): void;
  BackButton: {
    show(): void;
    hide(): void;
    onClick(handler: () => void): void;
    offClick(handler: () => void): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

/** Приложение открыто внутри Telegram: initData непустой только в реальном Mini App. */
export function isTelegramWebApp(): boolean {
  return Boolean(window.Telegram?.WebApp?.initData);
}

function requireWebApp(): TelegramWebApp {
  const app = window.Telegram?.WebApp;

  if (!app) {
    throw new Error('Telegram.WebApp недоступен: скрипт telegram-web-app.js не загружен');
  }

  return app;
}

function toViewport(app: TelegramWebApp): PlatformViewport {
  return {
    // viewportStableHeight не дёргается при системных панелях/клавиатуре —
    // это то, что не даёт полагаться на dvh внутри webview Telegram.
    height: app.viewportStableHeight || app.viewportHeight,
    isExpanded: app.isExpanded,
    safeAreaInsetBottom: 0,
  };
}

/** Статусы openInvoice: paid, cancelled, failed, pending; незнакомый — как сбой. */
function toPaymentResult(status: string): PaymentResult {
  return status === 'paid' || status === 'pending' || status === 'cancelled' ? status : 'failed';
}

export function createTelegramPlatform(): Platform {
  const app = requireWebApp();
  let backHandler: (() => void) | null = null;
  // initData снимается один раз при запуске и после согласия остаётся старым.
  let writeAccessGranted = false;

  return {
    kind: 'telegram',
    accountLinkRole: 'issuer',
    paymentKind: 'telegram-stars',

    ready() {
      app.ready();
      app.expand();
    },

    getIdentity() {
      return app.initData ? { platform: 'telegram', initData: app.initData } : null;
    },

    getStartParam() {
      return app.initDataUnsafe.start_param ?? null;
    },

    getTheme() {
      return { scheme: app.colorScheme };
    },

    subscribeTheme(listener) {
      const handler = () => listener({ scheme: app.colorScheme });

      app.onEvent('themeChanged', handler);
      return () => app.offEvent('themeChanged', handler);
    },

    setChrome({ background }) {
      // Оба метода не гарантированы на всех версиях клиента — деградируем молча.
      try {
        app.setHeaderColor?.(background);
        app.setBackgroundColor?.(background);
      } catch {
        // Неподдерживаемая версия Bot API — не критично.
      }
    },

    getViewport() {
      return toViewport(app);
    },

    subscribeViewport(listener) {
      const handler = () => listener(toViewport(app));

      app.onEvent('viewportChanged', handler);
      return () => app.offEvent('viewportChanged', handler);
    },

    subscribeVisibility: subscribeDocumentVisibility,

    setBackHandler(handler) {
      if (backHandler) {
        app.BackButton.offClick(backHandler);
      }

      backHandler = handler;

      if (handler) {
        app.BackButton.onClick(handler);
        app.BackButton.show();
      } else {
        app.BackButton.hide();
      }
    },

    close() {
      app.close();
    },

    async share(payload: SharePayload) {
      // Штатный механизм шаринга Telegram: открывает диалог выбора чата,
      // приложение при этом не закрывается.
      const text = payload.text ? `&text=${encodeURIComponent(payload.text)}` : '';
      app.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(payload.url)}${text}`);
    },

    restart(path) {
      window.location.assign(path);
    },

    openExternalLink(url) {
      app.openLink(url);
    },

    getMessageAccess() {
      if (typeof app.requestWriteAccess !== 'function') {
        return 'unsupported';
      }

      // allows_write_to_pm из initDataUnsafe — только подсказка для кнопки,
      // не идентификация: сервер о праве узнаёт из ответа Bot API.
      return writeAccessGranted || app.initDataUnsafe.user?.allows_write_to_pm === true
        ? 'granted'
        : 'not_granted';
    },

    requestMessageAccess() {
      const request = app.requestWriteAccess;

      if (typeof request !== 'function') {
        return Promise.resolve(false);
      }

      return new Promise<boolean>((resolve) => {
        request.call(app, (allowed) => {
          writeAccessGranted = writeAccessGranted || allowed;
          resolve(allowed);
        });
      });
    },

    startPayment(checkout) {
      if (!app.openInvoice) {
        return Promise.resolve('unsupported');
      }

      return new Promise<PaymentResult>((resolve) => {
        app.openInvoice?.(checkout.invoiceUrl, (status) => resolve(toPaymentResult(status)));
      });
    },
  };
}
